import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { api, fetchAvailability, rescheduleBooking } from "../lib/api";
import { Calendar } from "../components/ui/calendar";
import { format } from "date-fns";
import { toast } from "sonner";
import { Loader2, Check, ArrowRight } from "lucide-react";

const TIME_SLOTS = ["09:30", "10:30", "11:30", "13:00", "14:00", "15:00", "16:00", "17:00"];

export default function Reschedule() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const token = params.get("token");
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [date, setDate] = useState(null);
  const [time, setTime] = useState(null);
  const [booked, setBooked] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) { setLoading(false); return; }
    api.get(`/bookings/${id}/public?token=${token}`)
      .then((r) => setBooking(r.data))
      .catch(() => setBooking(null))
      .finally(() => setLoading(false));
  }, [id, token]);

  useEffect(() => {
    if (!date) return;
    fetchAvailability(format(date, "yyyy-MM-dd"))
      .then((r) => setBooked(r.booked_times || []))
      .catch(() => setBooked([]));
    setTime(null);
  }, [date]);

  const submit = async () => {
    setSubmitting(true);
    try {
      const r = await rescheduleBooking(id, token, format(date, "yyyy-MM-dd"), time);
      setBooking({ ...booking, date: r.date, time: r.time });
      setDone(true);
      toast.success("Rescheduled ♡");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not reschedule");
    } finally { setSubmitting(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-espresso" /></div>;
  if (!booking) return (
    <div className="pt-20 px-6 text-center" data-testid="resched-invalid">
      <h1 className="text-4xl font-serif text-espresso">Link not found</h1>
      <p className="text-mocha mt-3 text-sm">This reschedule link is invalid or expired.</p>
      <button onClick={() => navigate("/")} className="mt-6 px-6 py-3 rounded-full bg-espresso text-cream text-sm">Back home</button>
    </div>
  );

  if (done) return (
    <div className="pt-20 px-6 text-center" data-testid="resched-done">
      <div className="w-20 h-20 rounded-full bg-blush/40 mx-auto flex items-center justify-center shadow-glow"><Check className="text-espresso" /></div>
      <p className="script text-3xl text-gold mt-6">all changed</p>
      <h1 className="text-4xl font-serif text-espresso mt-1">See you {booking.date}</h1>
      <p className="text-mocha mt-3 text-sm">Your <strong>{booking.service_name}</strong> is now at <strong>{booking.time}</strong>.</p>
      <button onClick={() => navigate("/")} className="mt-6 px-6 py-3 rounded-full bg-espresso text-cream text-sm">Back home</button>
    </div>
  );

  return (
    <div className="pt-10 px-6 pb-24" data-testid="reschedule-page">
      <p className="script text-2xl text-gold">reschedule</p>
      <h1 className="text-4xl font-serif text-espresso leading-tight">Pick a new time</h1>
      <div className="mt-4 bg-creamDeep rounded-2xl p-4 text-sm">
        <p className="text-xs uppercase tracking-widest text-gold">Currently</p>
        <p className="text-espresso font-serif text-lg mt-1">{booking.service_name}</p>
        <p className="text-mocha">{booking.date} · {booking.time}</p>
      </div>

      <div className="mt-6 bg-white rounded-[2rem] p-4 shadow-soft flex justify-center">
        <Calendar mode="single" selected={date} onSelect={setDate}
          disabled={(d) => d < new Date(new Date().setHours(0,0,0,0)) || d.getDay() === 0} />
      </div>

      {date && (
        <>
          <p className="text-mocha text-sm mt-6 mb-3">New time on {format(date, "EEEE, MMMM d")}</p>
          <div className="grid grid-cols-3 gap-3">
            {TIME_SLOTS.map((t) => {
              const isBooked = booked.includes(t);
              return (
                <button key={t} onClick={() => !isBooked && setTime(t)} disabled={isBooked}
                  data-testid={`resched-time-${t}`}
                  className={`py-3 rounded-full border text-sm ${
                    isBooked ? "bg-creamDeep text-mocha/50 border-borderSoft line-through cursor-not-allowed"
                    : time === t ? "bg-espresso text-cream border-espresso" : "bg-white text-espresso border-borderSoft"
                  }`}>{t}</button>
              );
            })}
          </div>
        </>
      )}

      {date && time && (
        <button onClick={submit} disabled={submitting} data-testid="confirm-resched-btn"
          className="mt-8 w-full h-14 rounded-full bg-espresso text-cream text-sm flex items-center justify-center gap-2 shadow-glow">
          {submitting ? <Loader2 className="animate-spin" size={18} /> : <>Confirm new time <ArrowRight size={16} /></>}
        </button>
      )}
    </div>
  );
}
